import {
  Box,
  ListItemButton,
  ListItemContent,
  Stack,
  Typography,
} from "@mui/joy";
import dayjs from "dayjs";
import { ThoughtRecordType } from "api/thoughtRecordApi";
import { memo } from "react";
import useOpenThoughtRecord from "hooks/useOpenThoughtRecord";
import { theme } from "styles/theme";
import useIsRecordSeparatedPage from "../../../hooks/useIsRecordSeparatedPage";
import useUnifiedNavigate from "../../../hooks/useUnifiedNavigate";

function ThoughtRecordListItem({
  width,
  direction = "column",
  isFirstRecordOfMonth,
  thoughtRecord,
  isSelected,
  setCurrentThoughtRecord,
  shouldBeDone,
}: {
  width: string;
  direction?: "row" | "column";
  isFirstRecordOfMonth: boolean;
  thoughtRecord: ThoughtRecordType;
  isSelected?: boolean;
  setCurrentThoughtRecord: React.Dispatch<
    React.SetStateAction<ThoughtRecordType | undefined>
  >;
  shouldBeDone?: boolean;
}) {
  const { thoughtRecordKey, date, situation, isHighlight } = thoughtRecord;
  const openThoughtRecord = useOpenThoughtRecord(thoughtRecordKey);

  const isRecordSeparatedPage = useIsRecordSeparatedPage();
  const unifiedNavigate = useUnifiedNavigate();

  return (
    <Stack direction={direction} key={thoughtRecordKey}>
      {isFirstRecordOfMonth && (
        <Typography
          variant={"Caption"}
          sx={{
            width: width,
            color: theme.vars.palette.Content.Strong,
            pl: "12px",
            pt: "16px",
            pb: "8px",
            opacity: 0.5,
            fontWeight: 700,
          }}
        >
          {`${dayjs(date).format("MMM")}`}
        </Typography>
      )}
      <ListItemButton
        selected={isRecordSeparatedPage ? false : isSelected}
        sx={{
          width: width,
          p: 0,
          height: "80px",
          alignItems: "center",
          borderRadius: "16px",
          ...(isRecordSeparatedPage && {
            "@media (max-width: 1024px)": {
              "&:hover": {
                // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
                // ios : 버튼 클릭 후 touchend 해도 hover
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
                outline: "none !important",
              },
            },
            "&:hover, &.Mui-focusVisible, &.Mui-selected": {
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
              outline: "none !important",
            },
            "&:active": {
              backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`
            },
          }),
        }}
        onClick={() => {
          setCurrentThoughtRecord(thoughtRecord);
          (document.activeElement as HTMLElement)?.blur();

          if (isRecordSeparatedPage) {
            unifiedNavigate(`/thought-record-detail?trk=${thoughtRecordKey}`, {
              type: "card",
              animation: "default",
              gestureEnabled: true,
            });
          } else {
            openThoughtRecord();
          }
        }}
      >
        <ListItemContent>
          <Stack
            key={`thoughtRecord_${thoughtRecordKey}`}
            direction={"row"}
            alignItems="center"
            sx={{
              position: "relative",
            }}
          >
            {isHighlight && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "4px",
                  height: "100%",
                  borderRadius: "100px",
                  backgroundColor: theme.vars.palette.Border.Highlight,
                }}
              />
            )}

            <Stack
              direction={"column"}
              justifyContent={"center"}
              alignItems="center"
              sx={{
                ...(direction === "column" && {
                  ml: "8px",
                  mr: "12px",
                }),
                ...(direction === "row" && {
                  pl: "8px",
                }),
                width: "50px",
                minWidth: "50px",
              }}
            >
              <Typography
                variant={"H3"}
                sx={{
                  py: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 590,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).get("date")}
              </Typography>
              <Typography
                variant={"Caption"}
                sx={{
                  pb: 0,
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 400,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).format("ddd")}
              </Typography>
            </Stack>
            <Typography
              variant={"Body"}
              sx={{
                color: theme.vars.palette.Content.Strong,
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                pl: 0,
                pr: "20px",
                opacity: direction === "row" ? 0 : 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {situation}
            </Typography>
          </Stack>
        </ListItemContent>
      </ListItemButton>
    </Stack>
  );
}

export default memo(ThoughtRecordListItem);
